import { Chip } from '../common/Chip'
import { Icon } from '../common/Icon'
import { activeFilterCount, type FiltersState, type FilterGroup } from './filterState'
import type { FilterFacets } from '../../services/productRepository'

interface FilterPanelProps {
  facets: FilterFacets
  state: FiltersState
  onToggle: (group: FilterGroup, value: string) => void
  onClearAll: () => void
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-sand-200 py-5 first:border-t-0 first:pt-0">
      <h3 className="mb-3 text-eyebrow font-semibold uppercase tracking-[0.16em] text-ink-500">{title}</h3>
      {children}
    </div>
  )
}

export function FilterPanel({ facets, state, onToggle, onClearAll }: FilterPanelProps) {
  const count = activeFilterCount(state)
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 font-semibold text-ink">
          <Icon name="SlidersHorizontal" className="h-4 w-4" /> Filters
        </span>
        {count > 0 && (
          <button onClick={onClearAll} className="text-sm font-medium text-brand-600 hover:underline">
            Clear all
          </button>
        )}
      </div>

      {facets.categories.length > 0 && (
        <Group title="Category">
          <div className="flex flex-wrap gap-2">
            {facets.categories.map((c) => (
              <Chip
                key={c.slug}
                active={state.categories.includes(c.slug)}
                accent={c.accentColor}
                onClick={() => onToggle('categories', c.slug)}
              >
                {c.name}
              </Chip>
            ))}
          </div>
        </Group>
      )}

      {facets.benefits.length > 0 && (
        <Group title="Benefit">
          <div className="flex flex-wrap gap-2">
            {facets.benefits.map((b) => (
              <Chip key={b} active={state.benefits.includes(b)} onClick={() => onToggle('benefits', b)}>
                {b}
              </Chip>
            ))}
          </div>
        </Group>
      )}

      {facets.applications.length > 0 && (
        <Group title="Application">
          <div className="flex flex-wrap gap-2">
            {facets.applications.map((a) => (
              <Chip key={a} active={state.applications.includes(a)} onClick={() => onToggle('applications', a)}>
                {a}
              </Chip>
            ))}
          </div>
        </Group>
      )}

      {facets.crops.length > 0 && (
        <Group title="Crop">
          {/* No per-group height cap — the whole panel scrolls (desktop: the sidebar card; mobile: the
              drawer body), so the long crop list never looks clipped and every chip is reachable. */}
          <div className="flex flex-wrap gap-2">
            {facets.crops.map((c) => (
              <Chip key={c} active={state.crops.includes(c)} onClick={() => onToggle('crops', c)}>
                {c}
              </Chip>
            ))}
          </div>
        </Group>
      )}
    </div>
  )
}
